/**
 * API Configuration
 * 
 * Centralized API URL management
 * All API endpoints and base URLs are defined here
 */

// Base URLs
export const API_BASE_URL = "https://api.patientmesh.fr";


export const API_PREFIX = "/api";

// Full Base URL with prefix
export const API_URL = `${API_BASE_URL}${API_PREFIX}`;

// API Endpoints
export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN_DOCTOR: `${API_PREFIX}/auth/login-doctor`,
    TENANT_REGISTER: `${API_PREFIX}/auth/tenant/register`,
    TENANT_LOGIN: `${API_PREFIX}/auth/tenant/login`,
    TENANT_REGISTER_DOCTOR: `${API_PREFIX}/auth/tenant/register-doctor`,
    TENANT_INVITE_ADMIN: `${API_PREFIX}/auth/tenant/invite-admin`,
    ADMIN_REGISTER_DOCTOR: `${API_PREFIX}/auth/admin/register-doctor`,
    ADMIN_INVITE_DOCTOR: `${API_PREFIX}/auth/admin/invite-doctor`,
    COMPLETE_INVITATION: `${API_PREFIX}/auth/complete-invitation`,
    // Password Reset endpoints
    PASSWORD_FORGOT: `${API_PREFIX}/auth/password/forgot`,
    PASSWORD_VERIFY_OTP: `${API_PREFIX}/auth/password/verify-otp`,
    PASSWORD_RESET: `${API_PREFIX}/auth/password/reset`,
  },

  // Patient endpoints
  PATIENTS: {
    ADD: `${API_PREFIX}/patients/add`,
    ALL: `${API_PREFIX}/patients/all`,
    EXPORT_EXCEL: `${API_PREFIX}/patients/export/excel`,
    TENANT_ALL: `${API_PREFIX}/patients/tenant/all`,
    TENANT_EXPORT_EXCEL: `${API_PREFIX}/patients/tenant/export/excel`,
    BY_ID: (patientId) => `${API_PREFIX}/patients/${patientId}`,
    DELETE: (patientId) => `${API_PREFIX}/patients/${patientId}`,
    UPDATE: (patientId) => `${API_PREFIX}/patients/${patientId}`,
    INVITATIONS: (patientId) => `${API_PREFIX}/patients/${patientId}/invitations`,
    UPLOAD_FILES: (patientId) => `${API_PREFIX}/patients/${patientId}/upload-files`,
    UPLOAD_FILES_MULTIPLE: (patientId) => `${API_PREFIX}/patients/${patientId}/upload-files/multiple`,
    UPLOAD_FILE_BY_ID: (patientId, fileId) => `${API_PREFIX}/patients/${patientId}/upload-files/${fileId}`,
    DOWNLOAD_FILE: (patientId, fileId) => `${API_PREFIX}/patients/${patientId}/upload-files/${fileId}/download`,
  },

  // Doctor endpoints
  DOCTORS: {
    BY_ID: (doctorId) => `${API_PREFIX}/doctors/${doctorId}`,
    UPDATE: (doctorId) => `${API_PREFIX}/doctors/${doctorId}`,
    ALL: `${API_PREFIX}/doctors/all`,
    EXPORT_EXCEL: `${API_PREFIX}/doctors/export/excel`,
    TENANT_ALL: `${API_PREFIX}/doctors/tenant/all`,
    TENANT_EXPORT_EXCEL: `${API_PREFIX}/doctors/tenant/export/excel`,
    TENANT_BY_ID: (doctorId) => `${API_PREFIX}/doctors/tenant/${doctorId}`,
    TENANT_UPDATE: (doctorId) => `${API_PREFIX}/doctors/tenant/${doctorId}`,
    TENANT_DELETE: (doctorId) => `${API_PREFIX}/doctors/tenant/${doctorId}`,
  },

  // Tenant endpoints
  TENANTS: {
    BY_ID: (tenantId) => `${API_PREFIX}/tenants/${tenantId}`,
    UPDATE: (tenantId) => `${API_PREFIX}/tenants/${tenantId}`,
    PATIENT_BY_ID: (patientId) => `${API_PREFIX}/patients/tenant/${patientId}`,
  },

  // Form endpoints
  FORMS: {
    CREATE: `${API_PREFIX}/forms`,
    ALL: `${API_PREFIX}/forms`,
    BY_ID: (formId) => `${API_PREFIX}/forms/${formId}`,
    UPDATE: (formId) => `${API_PREFIX}/forms/${formId}`,
    DELETE: (formId) => `${API_PREFIX}/forms/${formId}`,
    INVITATIONS: (formId) => `${API_PREFIX}/forms/${formId}/invitations`,
  },

  // Form Answers endpoints
  FORM_ANSWERS: {
    CREATE: `${API_PREFIX}/form-answers`,
    ALL: `${API_PREFIX}/form-answers`,
    BY_ID: (formId) => `${API_PREFIX}/form-answers/${formId}`,
    EXPORT_EXCEL: (formId) => `${API_PREFIX}/form-answers/${formId}/export/excel`,
  },

  // Public Form endpoints
  PUBLIC: {
    FORM: (formToken) => `${API_PREFIX}/public/forms/${formToken}`,
    FORM_ANSWER: `${API_PREFIX}/public/form-answers`,
  },

  // File endpoints
  FILES: {
    CREATE: `${API_PREFIX}/files`,
    ALL: `${API_PREFIX}/files`,
    EXPORT_EXCEL: `${API_PREFIX}/files/export/excel`,
    BY_ID: (fileId) => `${API_PREFIX}/files/${fileId}`,
    UPDATE: (fileId) => `${API_PREFIX}/files/${fileId}`,
    DELETE: (fileId) => `${API_PREFIX}/files/${fileId}`,
    COPY: (fileId) => `${API_PREFIX}/files/${fileId}/copy`,
  },

  // MR (Medical Record) endpoints
  MR: {
    UPLOAD: `${API_PREFIX}/mr/upload`,
    BY_PATIENT: (patientId) => `${API_PREFIX}/mr/patient/${patientId}`,
    ANALYZE: (mrId, segmentType) => `${API_PREFIX}/mr/${mrId}/analyze?segmentType=${segmentType}`,
  },

  // Scoring Formula endpoints
  SCORING_FORMULAS: {
    CREATE: `${API_PREFIX}/scoring-formulas`,
    ALL: `${API_PREFIX}/scoring-formulas`,
    EXPORT_EXCEL: `${API_PREFIX}/scoring-formulas/export/excel`,
    BY_ID: (formulaId) => `${API_PREFIX}/scoring-formulas/${formulaId}`,
    UPDATE: (formulaId) => `${API_PREFIX}/scoring-formulas/${formulaId}`,
    DELETE: (formulaId) => `${API_PREFIX}/scoring-formulas/${formulaId}`,
    TEST: `${API_PREFIX}/scoring-formulas/test`,
  },

  // Permission Groups endpoints
  PERMISSION_GROUPS: {
    // Tenant routes (full access)
    ALL: `${API_PREFIX}/permission-groups`,
    BY_ID: (groupId) => `${API_PREFIX}/permission-groups/${groupId}`,
    CREATE: `${API_PREFIX}/permission-groups`,
    UPDATE: (groupId) => `${API_PREFIX}/permission-groups/${groupId}`,
    DELETE: (groupId) => `${API_PREFIX}/permission-groups/${groupId}`,
    
    // Admin doctor routes (with permission checks)
    ADMIN: {
      ALL: `${API_PREFIX}/permission-groups/admin/all`,
      BY_ID: (groupId) => `${API_PREFIX}/permission-groups/admin/${groupId}`,
      CREATE: `${API_PREFIX}/permission-groups/admin`,
      UPDATE: (groupId) => `${API_PREFIX}/permission-groups/admin/${groupId}`,
      DELETE: (groupId) => `${API_PREFIX}/permission-groups/admin/${groupId}`,
    },
    
    // Doctor assignment routes
    DOCTOR_GROUPS: (doctorId) => `${API_PREFIX}/permission-groups/doctor/${doctorId}`,
    ASSIGN_DOCTOR: `${API_PREFIX}/permission-groups/assign-doctor`,
    REMOVE_DOCTOR: (doctorId, groupId) => `${API_PREFIX}/permission-groups/remove-doctor/${doctorId}/${groupId}`,
  },

  // Stats endpoints
  STATS: {
    DOCTOR: `${API_PREFIX}/stats/doctor`,
    TENANT: `${API_PREFIX}/stats/tenant`,
    SUPER_ADMIN: `${API_PREFIX}/stats/super-admin`,
    GENERAL: `${API_PREFIX}/stats`,
  },

  // Subscription / plans / quotas endpoints
  SUBSCRIPTION: {
    CATALOG: `${API_PREFIX}/subscription/catalog`,
    PLANS: `${API_PREFIX}/subscription/plans`,
    CURRENT: `${API_PREFIX}/subscription/current`,
    ORDERS: `${API_PREFIX}/subscription/orders`,
    USAGE: `${API_PREFIX}/subscription/usage`,
    CHECKOUT_SIMULATE: `${API_PREFIX}/subscription/checkout-simulate`,
    CHANGE_SIMULATE: `${API_PREFIX}/subscription/change-simulate`,
    TENANT_NOTIFICATIONS: `${API_PREFIX}/subscription/tenant-notifications`,
    TENANT_NOTIFICATION_READ: (id) => `${API_PREFIX}/subscription/tenant-notifications/${id}/read`,

    SUPER_ADMIN: {
      PLANS: `${API_PREFIX}/subscription/super-admin/plans`,
      PLAN_UPSERT: (code) => `${API_PREFIX}/subscription/super-admin/plans/${code}`,
      BILLING_SETTINGS: `${API_PREFIX}/subscription/super-admin/billing-settings`,
      TENANT_CURRENT: (tenantId) => `${API_PREFIX}/subscription/super-admin/tenants/${tenantId}/current`,
      TENANT_USAGE: (tenantId) => `${API_PREFIX}/subscription/super-admin/tenants/${tenantId}/usage`,
      TENANT_ORDERS: (tenantId) => `${API_PREFIX}/subscription/super-admin/tenants/${tenantId}/orders`,
    },
  },

  // Super Admin endpoints
  SUPER_ADMIN: {
    LOGIN: `${API_PREFIX}/auth/super-admin/login`,
    STATS: `${API_PREFIX}/super-admin/stats`,
    TENANTS: {
      ALL: `${API_PREFIX}/super-admin/tenants`,
      BY_ID: (tenantId) => `${API_PREFIX}/super-admin/tenants/${tenantId}`,
      DELETE: (tenantId) => `${API_PREFIX}/super-admin/tenants/${tenantId}`,
      UPDATE_STATUS: (tenantId) => `${API_PREFIX}/super-admin/tenants/${tenantId}/active`,
    },
    DOCTORS: {
      ALL: `${API_PREFIX}/super-admin/doctors`,
      BY_ID: (doctorId) => `${API_PREFIX}/super-admin/doctors/${doctorId}`,
      DELETE: (doctorId) => `${API_PREFIX}/super-admin/doctors/${doctorId}`,
    },
    PATIENTS: {
      ALL: `${API_PREFIX}/super-admin/patients`,
      BY_ID: (patientId) => `${API_PREFIX}/super-admin/patients/${patientId}`,
      DELETE: (patientId) => `${API_PREFIX}/super-admin/patients/${patientId}`,
    },
    FORMS: {
      ALL: `${API_PREFIX}/super-admin/forms`,
      DELETE: (formId) => `${API_PREFIX}/super-admin/forms/${formId}`,
    },
    FORM_ANSWERS: {
      ALL: `${API_PREFIX}/super-admin/form-answers`,
      DELETE: (answerId) => `${API_PREFIX}/super-admin/form-answers/${answerId}`,
    },
    FORM_INVITATIONS: {
      ALL: `${API_PREFIX}/super-admin/form-invitations`,
      DELETE: (invitationId) => `${API_PREFIX}/super-admin/form-invitations/${invitationId}`,
    },
    SCORING_FORMULAS: {
      ALL: `${API_PREFIX}/super-admin/scoring-formulas`,
      DELETE: (formulaId) => `${API_PREFIX}/super-admin/scoring-formulas/${formulaId}`,
    },
    FILES: {
      ALL: `${API_PREFIX}/super-admin/files`,
      BY_ID: (fileId) => `${API_PREFIX}/super-admin/files/${fileId}`,
      DELETE: (fileId) => `${API_PREFIX}/super-admin/files/${fileId}`,
    },
    MRS: {
      ALL: `${API_PREFIX}/super-admin/mrs`,
      DELETE: (mrId) => `${API_PREFIX}/super-admin/mrs/${mrId}`,
    },
    DOCTOR_INVITATIONS: {
      ALL: `${API_PREFIX}/super-admin/doctor-invitations`,
      DELETE: (invitationId) => `${API_PREFIX}/super-admin/doctor-invitations/${invitationId}`,
    },
    PASSWORD_RESETS: {
      ALL: `${API_PREFIX}/super-admin/password-resets`,
      DELETE: (resetId) => `${API_PREFIX}/super-admin/password-resets/${resetId}`,
    },
    TRASH_LOGS: {
      ALL: `${API_PREFIX}/super-admin/trash-logs`,
      BY_ID: (logId) => `${API_PREFIX}/super-admin/trash-logs/${logId}`,
      REQUEST_OTP: `${API_PREFIX}/super-admin/rollback/request-otp`,
      ROLLBACK: `${API_PREFIX}/super-admin/rollback`,
    },
    EXPORTS: {
      RESOURCES: `${API_PREFIX}/super-admin/exports/resources`,
      EXCEL: `${API_PREFIX}/super-admin/exports/excel`,
    },
  },
};

// File/Upload URLs (for direct access, not through API)
export const UPLOAD_URLS = {
  PATIENTS: (filename) => `${API_BASE_URL}/uploads/patients/${filename}`,
  OUTPUT: (filename) => `${API_BASE_URL}/output/${filename}`,
};

export default {
  API_BASE_URL,
  API_PREFIX,
  API_URL,
  API_ENDPOINTS,
  UPLOAD_URLS,
};

